import React, { Component } from 'react'
import { groupBy, get } from 'lodash'
import styled from 'styled-components'
import { Segment, Header, Dropdown, Icon } from 'semantic-ui-react'
import PropTypes from 'prop-types'

import SensorChart from './SensorChart'

// Moment.js
var moment = require('moment')


const FlexWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
`

const StyledDropDown = styled(Dropdown) `
    margin-bottom: 20px;
    margin-right: 5px;
`

const resolutionOptions = [
    { text: '3 vuotta', value: '3v' },
    { text: '2 vuotta', value: '2v' },
    { text: '12 kuukautta', value: '12kk' },
    { text: '6 kuukautta', value: '6kk' },
    { text: '3 kuukautta', value: '3kk' },
    { text: '1 kuukausi', value: '1kk' },
    { text: '7 päivää', value: '7pv' },
    { text: '3 päivää', value: '3pv' },
    { text: '1 päivä', value: '1pv' }
]

class LocationMonitor extends Component {
    state = {
        selectedResolution: '7pv',
        selectedApartment: 'Koko rakennus',
        selectedRoom: 'Koko huoneisto',
        roomOptions: {},
        apartmentOptions: [],
        selectedData: []
    }

    componentDidMount() {
        // Check for the initial data
        this.checkData(this.state.selectedApartment, this.state.selectedRoom, this.state.selectedResolution)
            .then(selectedData => {
                this.setState({ selectedData: selectedData })
            })
    }

    componentWillReceiveProps(nextProps) {
        // Make apartment and room options when the data is ready    
        if (this.props.loadingInfo && !nextProps.loadingInfo) {
            var opts = this.makeOptions(nextProps.data.apartmentsAndRooms)
            this.setState({
                apartmentOptions: opts.apartmentOpts,
                roomOptions: opts.roomOpts
            })
        }
    }

    // Parses dropdown options from apartment and room data
    makeOptions = (apartmentsAndRooms) => {
        var roomOpts = {}
        var apartmentOpts = [{ text: 'Koko rakennus', value: 'Koko rakennus' }]
        var grouped = groupBy(apartmentsAndRooms, 'apartment')
        Object.keys(grouped).forEach(key => {
            apartmentOpts.push({ text: key, value: key })
            var options = grouped[key].map(obj => ({ text: obj.room, value: obj.room }))
            options.unshift({ text: 'Koko huoneisto', value: 'Koko huoneisto' })
            roomOpts[key] = options
        })
        return { roomOpts, apartmentOpts }
    }

    // Here we determine what data the user wants to see
    determineRequestedData = (selectedApartment, selectedRoom, selectedResolution) => {
        // These must be uppercase...used in dispatching redux actions dynamically
        var time = 'HOURLY'
        if (selectedResolution === '1kk' || selectedResolution === '3kk') {
            time = 'DAILY'
        }
        else if (selectedResolution === '6kk' || selectedResolution === '12kk') {
            time = 'WEEKLY'
        }
        else if (selectedResolution === '2v' || selectedResolution === '3v') {
            time = 'MONTHLY'
        }

        var apartment = 'all'
        if (selectedApartment !== 'Koko rakennus') apartment = selectedApartment

        var room = 'all'
        if (apartment !== 'all' && selectedRoom !== 'Koko huoneisto') {
            room = selectedRoom
        }

        return { time, apartment, room }
    }

    // This checks if the data is available
    // If it's not, make a request for it
    checkData = (selectedApartment, selectedRoom, selectedResolution) => {
        return new Promise((resolve, reject) => {
            var selectedData = this.getActiveData(selectedApartment, selectedRoom, selectedResolution)
            if (selectedData.length === 0) {
                var req = this.determineRequestedData(selectedApartment, selectedRoom, selectedResolution)
                this.props.requestData(req.apartment, req.room, req.time)
                    .then(() => {
                        var selectedData = this.getActiveData(selectedApartment, selectedRoom, selectedResolution)
                        resolve(selectedData)
                    })

            }
            else resolve(selectedData)
        })
    }

    // These are run when the filters change
    // They trigger the checks for data and set state afterwards
    hanldeApartmentChange = (e, data) => {
        this.checkData(data.value, this.state.selectedRoom, this.state.selectedResolution)
            .then(selectedData => {
                this.setState({ selectedApartment: data.value, selectedData: selectedData })
            })
    }
    hanldeRoomChange = (e, data) => {
        this.checkData(this.state.selectedApartment, data.value, this.state.selectedResolution)
            .then(selectedData => {
                this.setState({ selectedRoom: data.value, selectedData: selectedData })
            })
    }
    handleTimeChange = (e, data) => {
        this.checkData(this.state.selectedApartment, this.state.selectedRoom, data.value)
            .then(selectedData => {
                this.setState({ selectedResolution: data.value, selectedData: selectedData })
            })
    }

    // This determines and filters the selected data
    getActiveData = (selectedApartment, selectedRoom, selectedResolution) => {
        // First determine what data the user requested
        var req = this.determineRequestedData(selectedApartment, selectedRoom, selectedResolution)

        // Select the data container based on selection
        var dataContainer = this.props.data.hourly
        if (req.time === 'DAILY') dataContainer = this.props.data.daily
        if (req.time === 'WEEKLY') dataContainer = this.props.data.weekly
        if (req.time === 'MONTHLY') dataContainer = this.props.data.monthly

        // Filter the data further
        var data = dataContainer.building
        if (req.apartment !== 'all' && req.room === 'all') {
            data = get(dataContainer.apartment, selectedApartment) || []
        }
        else if (req.room !== 'all') {
            data = get(dataContainer.room, [selectedApartment + '_' + selectedRoom]) || []
        }

        // If there is no data, no point to continue filtering
        if (data.length === 0) {
            return []
        }

        // Date comparisons
        const filterByDate = (amount, unit) => {
            var compareDate = moment().subtract(amount, unit)
            return data.filter(reading => {
                return moment(reading.date).isSameOrAfter(compareDate)
            })
        }

        if (selectedResolution === '3v') return filterByDate(3, 'years')
        else if (selectedResolution === '2v') return filterByDate(2, 'years')
        else if (selectedResolution === '12kk') return filterByDate(12, 'months')
        else if (selectedResolution === '6kk') return filterByDate(6, 'months')
        else if (selectedResolution === '3kk') return filterByDate(3, 'months')
        else if (selectedResolution === '1kk') return filterByDate(1, 'months')
        else if (selectedResolution === '7pv') return filterByDate(7, 'days')
        else if (selectedResolution === '3pv') return filterByDate(3, 'days')
        else if (selectedResolution === '1pv') return filterByDate(1, 'days')
    }

    render() {
        const { location } = this.props
        const {
            selectedData,
            selectedResolution,
            selectedApartment,
            selectedRoom,
            apartmentOptions,
            roomOptions
        } = this.state

        var timeText = ''
        if (selectedResolution === '1pv' ||  selectedResolution === '3pv' || selectedResolution === '7pv') {
            timeText = '(keskiarvo tunneittain)'
            if (selectedApartment !== 'Koko rakennus' && selectedRoom !== 'Koko huoneisto') {
                timeText = '(mittaukset tunneittain)'
            }
        }
        else if (selectedResolution === '1kk' || selectedResolution === '3kk') {
            timeText = '(keskiarvo päivittäin)'
        }
        else if (selectedResolution === '6kk' || selectedResolution === '12kk') {
            timeText = '(keskiarvo viikoittain)'
        }
        else if (selectedResolution === '2v' || selectedResolution === '3v') {
            timeText = '(keskiarvo kuukausittain)'
        }

        const tempText = `Lämpötila ${timeText}`
        const humText = `Kosteus ${timeText}`

        return (
            <div>
                <Segment.Group>

                    <Segment>
                        <Header as="h3">{location.address + ', ' + location.city}</Header>
                    </Segment>

                    <Segment>
                        <FlexWrapper>
                            <div>
                                <Header as="h5">Huoneisto</Header>
                                <StyledDropDown
                                    selection
                                    value={selectedApartment}
                                    options={apartmentOptions}
                                    onChange={this.hanldeApartmentChange}
                                />
                            </div>

                            {
                                selectedApartment === 'Koko rakennus' ? <span></span> :
                                    <div>
                                        <Header as="h5">Huone</Header>
                                        <StyledDropDown
                                            selection
                                            value={selectedRoom}
                                            options={roomOptions[selectedApartment]}
                                            onChange={this.hanldeRoomChange}
                                        />
                                    </div>
                            }

                            <div>
                                <Header as="h5">Aikaväli</Header>
                                <StyledDropDown
                                    selection
                                    value={selectedResolution}
                                    options={resolutionOptions}
                                    onChange={this.handleTimeChange}
                                />
                            </div>
                        </FlexWrapper>
                    </Segment>
                </Segment.Group>

                <Segment>
                    <Header as="h5">
                        <Icon name="thermometer half" />
                        {tempText}
                    </Header>
                    <SensorChart data={selectedData} keyName='temp' name='lämpö' unit='&#8451;' />

                    <Header as="h5">
                        <Icon name="cloud" />
                        {humText}
                    </Header>
                    <SensorChart data={selectedData} keyName='hum' name='kosteus' unit='%' />
                </Segment>

            </div>
        )
    }
}

LocationMonitor.defaultProps = {
    location: {
        city: '',
        address: ''
    },
    data: {
        apartmentsAndRooms: [],
        daily: {
            building: [],
            apartment: {},
            room: {}
        },
        hourly: {
            building: [],
            apartment: {},
            room: {}
        }
    }
}
LocationMonitor.propTypes = {
    location: PropTypes.shape({
        location: PropTypes.string,
        address: PropTypes.string
    }),
    data: PropTypes.shape({
        apartmentsAndRooms: PropTypes.array,
        daily: PropTypes.shape({
            building: PropTypes.array,
            apartment: PropTypes.object,
            room: PropTypes.object
        }),
        hourly: PropTypes.shape({
            building: PropTypes.array,
            apartment: PropTypes.object,
            room: PropTypes.object
        })
    })

}

export default LocationMonitor


// WEBPACK FOOTER //
// ./src/Shared/LocationMonitor.js